/**
 * Article rédigé le 2026-09-06 à partir de sources web ouvertes et vérifiées automatiquement
 * (5 URL testées, révisé après vérification). Daté au moment des faits : 2026-02-06.
 * À relire avant/après mise en ligne. Format : voir src/blog/README.md
 */
export default {
  "slug": "seminaire-ia-academie-supmicrotech-besancon-2026",
  "title": "« Nous, nos métiers et l’IA » : l’académie réunit ses personnels à SUPMICROTECH",
  "excerpt": "Le 4 février 2026, SUPMICROTECH-ENSMM a accueilli le séminaire régional IA de l’Éducation nationale, retransmis vers sept autres sites : deux conférences, puis des ateliers sur le prompt.",
  "seoTitle": "Séminaire IA de l’académie à SUPMICROTECH",
  "seoDescription": "Le 4 février 2026, SUPMICROTECH-ENSMM a accueilli le séminaire régional de l’Éducation nationale consacré à l’IA, retransmis vers sept autres sites en BFC.",
  "category": "formation",
  "tags": [
    "SUPMICROTECH-ENSMM",
    "DRANE",
    "Éducation nationale",
    "IA générative",
    "Réseau Canopé"
  ],
  "author": "jimmy",
  "publishedAt": "2026-02-06",
  "updatedAt": "2026-02-06",
  "featured": false,
  "cover": {
    "image": "/img/blog/20100220-temis-101-4373028055.jpg",
    "credit": "Photo : Le technopôle Temis à Besançon — Régions Démocrates 2010, CC BY 2.0, via Wikimedia Commons",
    "alt": "Vue du technopôle Temis à Besançon : à gauche, le long bâtiment aux brise-soleil d'aluminium de SUPMICROTECH-ENSMM, où s'est tenu le séminaire régional consacré à l'intelligence artificielle."
  },
  "content": [
    {
      "type": "lead",
      "text": "« Nous, nos métiers et l’intelligence artificielle » : sous ce titre, le séminaire régional de l’Éducation nationale s’est tenu le mercredi 4 février 2026 dans les locaux de [SUPMICROTECH-ENSMM](/blog/se-former-ia-besancon-guide-voies-acces), à Besançon, de 8 h 45 à 16 h[^1]. Retransmise vers sept autres lieux de Bourgogne-Franche-Comté, la journée réunissait enseignants, cadres et personnels des services autour de deux conférences et d’ateliers pratiques[^1]. Le temps d’une journée, l’école d’ingénieurs bisontine est devenue l’amphithéâtre de formation à l’IA de la région académique."
    },
    {
      "type": "keyfacts",
      "facts": [
        {
          "label": "Quoi",
          "value": "Séminaire régional « Nous, nos métiers et l’intelligence artificielle »"
        },
        {
          "label": "Quand",
          "value": "Mercredi 4 février 2026, de 8 h 45 à 16 h"
        },
        {
          "label": "Où",
          "value": "SUPMICROTECH-ENSMM, Besançon, avec retransmission sur sept autres sites"
        },
        {
          "label": "Qui",
          "value": "DRANE, DRASI, EAFC et Réseau Canopé"
        },
        {
          "label": "Pour qui",
          "value": "Les personnels de l’Éducation nationale de la région académique, en établissement ou en service déconcentré"
        }
      ]
    },
    {
      "type": "h2",
      "text": "Besançon, tête de réseau d’une journée à huit sites"
    },
    {
      "type": "p",
      "text": "Le séminaire était co-organisé par la délégation régionale académique au numérique pour l’éducation (DRANE), la direction régionale académique des systèmes d’information (DRASI), les écoles académiques de la formation continue (EAFC) et Réseau Canopé[^1]. Les interventions de la matinée ont été retransmises en direct à l’ESTP-Campus de Dijon et dans les Ateliers Canopé de Lons-le-Saunier, Nevers, Vesoul, Mâcon, Auxerre et Belfort, soit huit sites d’accueil au total[^1]. L’après-midi, les ateliers ont été reproduits à l’identique sur chacun de ces sites[^1]."
    },
    {
      "type": "h2",
      "text": "Deux conférences : l’éthique avant les outils"
    },
    {
      "type": "p",
      "text": "La première conférence est revenue à **Alexei Grinbaum**, sous un titre qui annonçait la couleur : « … Et le nombre s’est fait verbe : éthique de l’IA générative »[^1]. Directeur de recherche au CEA-Saclay et président de son comité opérationnel d’éthique du numérique, ce spécialiste de la théorie de l’information quantique travaille depuis 2003 sur les questions éthiques soulevées par les nouvelles technologies ; il a publié *Parole de machines* (Humensciences, 2023) et *Les robots et le mal* (Desclée de Brouwer, 2019)[^1]."
    },
    {
      "type": "p",
      "text": "**Axel Jean** a pris le relais sous l’intitulé « Éducation et IA : différenciations et repères pour l’action »[^1]. Chef du bureau de soutien à l’innovation numérique et à la recherche appliquée à la direction du numérique pour l’éducation, il y suit les dossiers relatifs à l’IA[^1]. La matinée s’était ouverte par les mots d’accueil de Christophe Varnier pour SUPMICROTECH-ENSMM, de Samuel Vitel pour Réseau Canopé, des rectrices Mathilde Gollety et Nathalie Albert-Moretti, et d’Audran Le Baron, directeur du numérique pour l’éducation[^1]. Le programme prévoyait ensuite un temps d’échange entre les participants des huit sites et les deux conférenciers[^1]."
    },
    {
      "type": "h2",
      "text": "L’après-midi, deux niveaux d’ateliers autour du prompt"
    },
    {
      "type": "p",
      "text": "La séquence pratique portait un intitulé sans ambiguïté : « Optimiser nos pratiques professionnelles grâce aux IA génératives »[^1]. Deux formats coexistaient selon le degré de familiarité de chacun avec ces outils, avec un fil conducteur commun : la formulation de requête, le *prompt*, appliquée aux gestes professionnels du quotidien[^1]."
    },
    {
      "type": "ul",
      "items": [
        "**Je découvre les IA génératives** : identifier la terminologie liée à l’intelligence artificielle, expérimenter un outil transférable dans son activité professionnelle, comprendre le Cadre d’usage de l’IA en éducation et les principes du RGPD[^1]",
        "**Je me perfectionne avec les IA génératives** : partager et interroger ses pratiques professionnelles, exploiter le potentiel de l’IA au regard des normes juridiques en vigueur, explorer les perspectives d’évolution des technologies d’IA et leurs effets sur les métiers de l’éducation[^1]"
      ]
    },
    {
      "type": "p",
      "text": "L’animation de ces ateliers collaboratifs était confiée à des conseillers au numérique de la DRANE, à des formateurs de la DRASI et de Réseau Canopé ainsi qu’à des formateurs académiques[^1]. Au-delà des usages, le programme annonçait un travail sur le mécanisme des différents modèles d’IA générative[^1]."
    },
    {
      "type": "h2",
      "text": "Ce que l’académie retient pour les classes"
    },
    {
      "type": "p",
      "text": "Les ateliers renvoyaient au [Cadre d’usage de l’IA en éducation](https://drne.region-academique-bourgogne-franche-comte.fr/fondamentaux-cadre-usage-ia/), annoncé en février 2025 et publié en juin 2025, qui repose sur cinq principes : plus-value pédagogique, protection des données saisies dans les outils, prise en compte de l’impact environnemental, transparence sur le recours à ces outils et développement de l’esprit critique[^3]. Le texte fixe aussi une limite côté élèves : l’usage autonome est autorisé à partir de la classe de quatrième, et recourir à une IA générative pour un devoir sans autorisation explicite et sans travail personnel d’appropriation relève de la fraude scolaire[^3]."
    },
    {
      "type": "p",
      "text": "Le dossier thématique de la DRANE « Les IA en éducation », dont la version PDF est datée de janvier 2026, distingue pour sa part IA prédictive et IA générative et recense les apports envisageables, de la différenciation à l’allègement de tâches administratives, sans masquer les limites : hallucinations, biais hérités des données d’entraînement, absence de compréhension réelle[^2]. Il rappelle que le parcours Pix IA est obligatoire depuis janvier 2026 pour les élèves de quatrième, de seconde et de première année de CAP[^2]."
    },
    {
      "type": "callout",
      "tone": "tip",
      "title": "Pourquoi ça compte pour Besançon",
      "text": "En prêtant ses locaux, SUPMICROTECH-ENSMM ajoute une corde à son arc local. L’école d’ingénieurs, établissement-composante de l’Université Marie et Louis Pasteur, accueillait 795 étudiants en 2024-2025 et héberge trois départements de recherche de [l’institut FEMTO-ST](/blog/femto-st-besancon-capitale-microtechniques), entre mécanique, mécatronique et microsystèmes[^5]. Le bassin bisontin ne forme donc pas seulement des ingénieurs à ces technologies : il devient aussi le lieu où l’on forme celles et ceux qui en parleront aux élèves."
    },
    {
      "type": "h2",
      "text": "Reste à en mesurer les effets"
    },
    {
      "type": "p",
      "text": "Les participants pouvaient demander deux open badges BRAVO BFC, l’un attestant de leur présence (« J’y étais »), l’autre de leur participation à l’atelier choisi (« J’ai participé »)[^1]. Aucun bilan de fréquentation ni support d’atelier n’a été publié à ce jour. Le séminaire du 22 janvier 2025, consacré au thème « L’intelligence artificielle, opportunités pour la pédagogie et les apprentissages ? », avait donné lieu à la mise en ligne d’un replay, des supports d’ateliers et de six podcasts accompagnés de leurs transcriptions[^4] : un précédent qui laisse espérer, pour la journée bisontine, une trace consultable au-delà des huit amphithéâtres du 4 février."
    }
  ],
  "references": [
    {
      "id": 1,
      "title": "Événements – Séminaire IA 2026 : « Nous, nos métiers et l’intelligence artificielle »",
      "publisher": "DRANE Bourgogne-Franche-Comté",
      "url": "https://drne.region-academique-bourgogne-franche-comte.fr/evenements-seminaire-ia-2026/",
      "type": "official",
      "date": ""
    },
    {
      "id": 2,
      "title": "Dossier thématique – Les IA en éducation",
      "publisher": "DRANE Bourgogne-Franche-Comté",
      "url": "https://drne.region-academique-bourgogne-franche-comte.fr/dossiers_les-ia-en-education/",
      "type": "official",
      "date": "2025-12-18"
    },
    {
      "id": 3,
      "title": "Fondamentaux – Cadre d’usage de l’IA en éducation",
      "publisher": "DRANE Bourgogne-Franche-Comté",
      "url": "https://drne.region-academique-bourgogne-franche-comte.fr/fondamentaux-cadre-usage-ia/",
      "type": "official",
      "date": ""
    },
    {
      "id": 4,
      "title": "Restitution du séminaire IA 2025 – « L’intelligence artificielle, opportunités pour la pédagogie et les apprentissages ? »",
      "publisher": "DRANE Bourgogne-Franche-Comté",
      "url": "https://drne.region-academique-bourgogne-franche-comte.fr/evenements-seminaire-ia-2024-25-restitution/",
      "type": "official",
      "date": "2025-01-22"
    },
    {
      "id": 5,
      "title": "SUPMICROTECH-ENSMM – présentation de l’école",
      "publisher": "SUPMICROTECH-ENSMM",
      "url": "https://www.supmicrotech.fr/",
      "type": "official",
      "date": ""
    }
  ]
};
